const storageKey = 'ai-assistant-launcher-position'
const margin = 12
const clamp = (value, max) => Math.max(0, Math.min(value, max))

// Remember the docked side and relative height across viewport sizes.
export function attachLauncher(el, win = window) {
  let position = null, gesture = null, suppressClick = false
  try {
    const saved = JSON.parse(win.localStorage.getItem(storageKey))
    if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) {
      position = { x: clamp(saved.x, 1), y: clamp(saved.y, 1) }
    }
  } catch (_) { /* Position storage is optional. */ }

  function bounds() {
    const rect = el.getBoundingClientRect()
    // innerWidth includes the scrollbar; fixed elements use the content viewport.
    const viewport = win.document && win.document.documentElement
    const width = (viewport && viewport.clientWidth) || win.innerWidth
    const height = (viewport && viewport.clientHeight) || win.innerHeight
    return { rect, x: Math.max(0, width - rect.width), y: Math.max(0, height - rect.height - margin * 2) }
  }
  function place(left, top, limits) {
    const x = clamp(left, limits.x), y = clamp(top - margin, limits.y)
    Object.assign(el.style, { left: `${x}px`, top: `${y + margin}px`, right: 'auto', bottom: 'auto' })
    position = { x: limits.x ? x / limits.x : 0, y: limits.y ? y / limits.y : 0 }
  }
  function dock(limits) {
    const side = position ? (position.x < 0.5 ? 0 : 1) : 1
    const top = position ? margin + position.y * limits.y : limits.rect.top
    place(side * limits.x, top, limits)
    // Keep the chosen edge anchored when fonts load or a drawer hides the scrollbar.
    Object.assign(el.style, side ? { left: 'auto', right: '0px' } : { left: '0px', right: 'auto' })
    el.classList.add(side ? 'is-docked-right' : 'is-docked-left')
    el.classList.remove(side ? 'is-docked-left' : 'is-docked-right')
  }
  function refresh() {
    if (gesture) return
    const limits = bounds()
    if (!limits.rect.width || !limits.rect.height) return
    dock(limits)
  }
  function move(event) {
    if (!gesture || event.pointerId !== gesture.id) return
    const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y
    if (!gesture.moved && Math.hypot(dx, dy) < 6) return
    gesture.moved = true
    suppressClick = true
    el.classList.add('is-dragging')
    el.classList.remove('is-docked-left', 'is-docked-right')
    event.preventDefault()
    place(gesture.left + dx, gesture.top + dy, bounds())
  }
  function finish(event) {
    if (!gesture || (event && event.pointerId !== gesture.id)) return
    const previous = gesture
    gesture = null
    el.classList.remove('is-dragging')
    win.removeEventListener('pointermove', move)
    win.removeEventListener('pointerup', finish)
    win.removeEventListener('pointercancel', cancel)
    if (el.hasPointerCapture(previous.id)) el.releasePointerCapture(previous.id)
    if (previous.moved && position) {
      dock(bounds())
      try { win.localStorage.setItem(storageKey, JSON.stringify(position)) } catch (_) { /* Storage may be disabled. */ }
    }
  }
  function cancel(event) {
    if (!gesture || (event && event.pointerId !== gesture.id)) return
    suppressClick = true
    finish(event)
  }
  function down(event) {
    if (gesture || !event.isPrimary || event.button !== 0) return
    suppressClick = false
    const rect = el.getBoundingClientRect()
    gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, moved: false }
    el.setPointerCapture(event.pointerId)
    win.addEventListener('pointermove', move, { passive: false })
    win.addEventListener('pointerup', finish)
    win.addEventListener('pointercancel', cancel)
  }
  function click(event) {
    // Keyboard activation remains available after dragging or cancelling a touch.
    if (suppressClick && event.detail !== 0) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }
  function resize() { cancel(); refresh() }
  el.addEventListener('pointerdown', down)
  el.addEventListener('lostpointercapture', cancel)
  el.addEventListener('click', click, true)
  win.addEventListener('resize', resize)
  refresh()
  return {
    refresh,
    destroy() {
      cancel()
      el.removeEventListener('pointerdown', down)
      el.removeEventListener('lostpointercapture', cancel)
      el.removeEventListener('click', click, true)
      win.removeEventListener('resize', resize)
    }
  }
}

const launchers = new WeakMap()
export default {
  inserted(el) { launchers.set(el, attachLauncher(el)) },
  componentUpdated(el) { const launcher = launchers.get(el); if (launcher) launcher.refresh() },
  unbind(el) { const launcher = launchers.get(el); if (launcher) launcher.destroy(); launchers.delete(el) }
}
